import { useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import websocketClient from './client';
import { fetchMoves } from '../store/moves';
import { fetchTrailersUser } from '../store/trailers';

export const useWebSocket = () => {
  const dispatch = useDispatch();
  const sessionUser = useSelector(state => state.session.user);
  const selectedAccountId = useSelector(state => state.ui.selectedAccountId);

  const handleMoveStatusChanged = useCallback((data) => {
    console.log('Handling move status change:', data);
    
    // Refresh moves data
    if (sessionUser?.is_admin && selectedAccountId) {
      dispatch(fetchMoves(selectedAccountId));
    } else {
      dispatch(fetchMoves());
    }

    // Refresh trailers data if affected trailers
    if (data.affected_trailers && data.affected_trailers.length > 0) {
      const accountId = sessionUser?.is_admin && selectedAccountId ? selectedAccountId : sessionUser?.account_id;
      if (accountId) {
        dispatch(fetchTrailersUser(accountId));
      }
    }
  }, [dispatch, sessionUser, selectedAccountId]);

  const handleTrailerLocationUpdated = useCallback((data) => {
    console.log('Handling trailer location update:', data);
    
    // Refresh trailers data
    const accountId = sessionUser?.is_admin && selectedAccountId ? selectedAccountId : sessionUser?.account_id;
    if (accountId) {
      dispatch(fetchTrailersUser(accountId));
    }
  }, [dispatch, sessionUser, selectedAccountId]);

  const handleLocationHistoryUpdated = useCallback((data) => {
    console.log('Handling location history update:', data);
    
    // Refresh location history data
    // This would trigger a refresh in the Home component
    // We can add a custom event or use a different approach
    window.dispatchEvent(new CustomEvent('locationHistoryUpdated', { detail: data }));
  }, []);

  useEffect(() => {
    if (sessionUser?.id) {
      // Connect to WebSocket when user is authenticated
      websocketClient.connect();

      // Add event handlers
      websocketClient.addEventHandler('move_status_changed', handleMoveStatusChanged);
      websocketClient.addEventHandler('trailer_location_updated', handleTrailerLocationUpdated);
      websocketClient.addEventHandler('location_history_updated', handleLocationHistoryUpdated);

      return () => {
        // Remove event handlers on cleanup
        websocketClient.removeEventHandler('move_status_changed', handleMoveStatusChanged);
        websocketClient.removeEventHandler('trailer_location_updated', handleTrailerLocationUpdated);
        websocketClient.removeEventHandler('location_history_updated', handleLocationHistoryUpdated);
        
        // Note: We don't disconnect here because other components might be using it
        // WebSocket will disconnect when the user logs out or page is closed
      };
    } else {
      // Disconnect if user is not authenticated
      websocketClient.disconnect();
    }
  }, [sessionUser, handleMoveStatusChanged, handleTrailerLocationUpdated, handleLocationHistoryUpdated]);

  return {
    isConnected: websocketClient.getConnectionStatus(),
    connect: () => websocketClient.connect(),
    disconnect: () => websocketClient.disconnect()
  };
};